import {
  ConsumoDashboardDto,
  ConsumoDto,
  ConsumoPdfQueryParams,
  PdfConsumoUnidadeResponse,
  ConsumoUnidade,
} from "core/features/consumo/typings";
import { UnidadeSimplesPaginateResponse } from "features/unidades/typings/unidade";
import { defaultContentMapperXlsx } from "infra/packages/json-as-xlsx/index";
import xlsx, { IColumn, IContent, IJsonSheet, ISettings } from "json-as-xlsx";
import moment from "moment";
import { mapTipoConsumo } from "../components/ListarConsumo";
import { getValorConsumo } from "core/features/consumo/helpers/getValorAplicadoConsumo";

export function exportExcelExemploImportacaoConsumo(
  unidades: UnidadeSimplesPaginateResponse[]
) {
  const columns: IColumn[] = [];

  if (unidades.some((unidade) => unidade.blocoQuadra)) {
    columns.push({
      label: "blocoQuadra",
      value: (row: IContent) =>
        (row as unknown as { numero: string; blocoQuadra: string }).blocoQuadra,
    });
  }

  columns.push({
    label: "numero",
    value: (row: IContent) =>
      (row as unknown as { numero: string; blocoQuadra: string }).numero,
  });

  columns.push({
    label: "medidaAtual",
    format: "#,###0.00",
    value: "",
  });

  const settings: ISettings = {
    fileName: `Tabela Exemplo - Consumo por Unidade`,
    extraLength: 3,
    writeOptions: {},
  };

  const sheets: IJsonSheet[] = [];

  sheets.push({
    sheet: `Consumo por unidade`,
    columns: columns,
    content: unidades.map((unidade) => ({
      blocoQuadra: unidade.blocoQuadra?.nome ?? "",
      numero: unidade.numero,
    })),
  });

  xlsx(sheets, settings);
}

export function exportExcelPdfConsumo({
  items,
  consumo,
  filtro,
  dashboard,
  unidadeMedida,
}: PdfConsumoUnidadeResponse & {
  consumo: ConsumoDto;
  filtro: ConsumoPdfQueryParams;
  dashboard: ConsumoDashboardDto;
  unidadeMedida?: string;
}) {
  const settings: ISettings = {
    fileName: consumo?.tipoConsumoId
      ? `Relatório de Consumo de ${mapTipoConsumo[consumo?.tipoConsumoId]}`
      : "Relatório de Consumo",
    extraLength: 3,
    writeOptions: {},
  };

  const columnsResumo = getColumnsResumo(filtro, unidadeMedida);

  const columns = getColumns(consumo, filtro, unidadeMedida);

  const sheets: IJsonSheet[] = [];

  sheets.push({
    sheet: "Resumo",
    columns: columnsResumo,
    content: [dashboard].map(defaultContentMapperXlsx),
  });

  sheets.push({
    sheet: "Unidades",
    columns: columns,
    content: items.map((item) => ({
      consumoId: item.consumoId,
      blocoQuadra: item.blocoQuadra ?? "",
      consumoUnidadeMesAnterior: item.consumoUnidadeMesAnterior ?? 0,
      numero: item.numero,
      medidaAtual: item.medidaAtual ?? 0,
      unidadeId: item.unidadeId,
    })),
  });

  xlsx(sheets, settings);
}

function getColumns(
  consumo: ConsumoDto,
  filtro: ConsumoPdfQueryParams,
  unidadeMedida?: string
) {
  const columns: IColumn[] = [];

  const mesAtual = moment(filtro.mes, "YYYY-MM").format("MMMM");
  const mesAnterior = moment(filtro.mes, "YYYY-MM")
    .subtract(1, "month")
    .format("MMMM");

  columns.push({
    label: "Unidade",
    value: (row: IContent) => {
      const original = row as unknown as ConsumoUnidade;

      if (original.blocoQuadra) {
        return `${original.blocoQuadra}, ${original.numero}`;
      }

      return `${original.numero}`;
    },
  });

  columns.push({
    label: `Medição em ${mesAnterior} (${unidadeMedida})`,
    value: (row: IContent) =>
      `${(
        (row as unknown as ConsumoUnidade).consumoUnidadeMesAnterior ?? 0
      ).toFixed(3)}`,
  });

  columns.push({
    label: `Medição em ${mesAtual} (${unidadeMedida})`,
    value: (row: IContent) =>
      `${((row as unknown as ConsumoUnidade).medidaAtual ?? 0).toFixed(3)}`,
  });

  columns.push({
    label: `Consumo em ${mesAtual} (${unidadeMedida})`,
    value: (row: IContent) => {
      const original = row as unknown as ConsumoUnidade;

      const medida = Math.max(
        (original.medidaAtual ?? 0) - (original.consumoUnidadeMesAnterior ?? 0),
        0
      );

      return medida.toFixed(3);
    },
  });

  columns.push({
    label: "Valor (R$)",
    format: "#,###0.00",
    value: (row: IContent) => {
      const original = row as unknown as ConsumoUnidade;

      const diferencaConsumo = Math.max(
        (original.medidaAtual ?? 0) - (original.consumoUnidadeMesAnterior ?? 0),
        0
      );

      const valorMonetario = getValorConsumo(diferencaConsumo, consumo);

      return valorMonetario;
    },
  });

  return columns;
}

function getColumnsResumo(
  filtro: ConsumoPdfQueryParams,
  unidadeMedida?: string
) {
  const columns: IColumn[] = [];

  const mesAtual = moment(filtro.mes, "YYYY-MM").format("MM/YYYY");

  columns.push({
    label: "Mês",
    value: () => mesAtual,
  });

  columns.push({
    label: "Medição total",
    value: (row: IContent) =>
      `${
        (row as unknown as ConsumoDashboardDto)?.somaTotal ?? 0
      } ${unidadeMedida}`,
  });

  columns.push({
    label: "Unidades medidas",
    value: (row: IContent) =>
      `${(row as unknown as ConsumoDashboardDto)?.unidadesComMedida ?? 0}`,
  });

  columns.push({
    label: "Unidades a medir",
    value: (row: IContent) =>
      `${
        ((row as unknown as ConsumoDashboardDto)?.totalUnidades ?? 0) -
        ((row as unknown as ConsumoDashboardDto)?.unidadesComMedida ?? 0)
      }`,
  });

  columns.push({
    label: "Consumo total",
    value: (row: IContent) =>
      `${
        (row as unknown as ConsumoDashboardDto)?.totalConsumo ?? 0
      } ${unidadeMedida}`,
  });

  columns.push({
    label: "Valor total a cobrar (R$)",
    format: "#,###0.00",
    value: (row: IContent) =>
      (row as unknown as ConsumoDashboardDto)?.valorCobradoTotal,
  });

  return columns;
}
